import { createModal } from '/@/core/elements/modal'
import {
  type Mockup,
  type Palette,
  // Image
} from '../../things/types'
import { type ViewerType } from '/@/utils/misc'
import { type Portfolio, type PortfolioProject } from '/@/pages/Portfolio/types'
import { type Project } from '/@/common/projects/types'
import { type Network } from '/@/common/share/types'

export type ShareMode =
  | Network
  | 'submit to client'
  | 'apartment therapy'
  | 'design challenge'

export type ResourceType =
  | 'vizi'
  | 'palette'
  | 'portfolio'
  | 'portfolioProject'
  | 'project'

type ShareEntityType =
  | 'MOCKUP'
  | 'PALETTE'
  | 'PORTFOLIO'
  | 'PORTFOLIO_PROJECT'
  | 'PROJECT'

export interface RichShareModalData {
  resourceType: ResourceType
  url: string
  title: string
  imageTitle: string
  imageExtension: 'jpg' | 'png'
  description: string
  viewer: ViewerType
  modes: ShareMode[]

  // depends on the resource type
  content: {
    // image?: Pick<Image, 'id' | 'url'> | null
    vizi?: Pick<Mockup, 'id' | 'imageUrl' | 'mockupType'> | null
    palette?: Pick<Palette, 'id' | 'colors'> | null
    portfolio?: Pick<Portfolio, 'id' | 'imageUrl'> | null
    portfolioProject?: Pick<PortfolioProject, 'id' | 'imageUrl'> | null
    project?: (Pick<Project, 'id'> & { imageUrl?: string }) | null
  }
  onSuccess: ((shareMode: ShareMode) => void) | null
}

const resourceToEntityMap: Record<ResourceType, ShareEntityType> = {
  vizi: 'MOCKUP',
  palette: 'PALETTE',
  portfolio: 'PORTFOLIO',
  portfolioProject: 'PORTFOLIO_PROJECT',
  project: 'PROJECT',
}

export function getShareEntityType(type: ResourceType) {
  return resourceToEntityMap[type]
}

export function getContent(
  contents: RichShareModalData['content'],
  type: 'vizi',
): Pick<Mockup, 'id' | 'imageUrl' | 'mockupType'>
export function getContent(
  contents: RichShareModalData['content'],
  type: 'palette',
): Pick<Palette, 'id' | 'colors'>
export function getContent(
  contents: RichShareModalData['content'],
  type: 'portfolio',
): Pick<Portfolio, 'id' | 'imageUrl'>
export function getContent(
  contents: RichShareModalData['content'],
  type: 'portfolioProject',
): Pick<PortfolioProject, 'id' | 'imageUrl'>
export function getContent(
  contents: RichShareModalData['content'],
  type: 'project',
): Pick<Project, 'id'> & { imageUrl?: string }
export function getContent(
  contents: RichShareModalData['content'],
  type: ResourceType,
): { id: string }
export function getContent(
  contents: RichShareModalData['content'],
  type: ResourceType,
) {
  if (!type) throw Error('Failed to share. Resource type was undefined.')
  const content = contents[type]
  if (!content)
    throw Error(
      `Failed to share. No content provided for resource type "${type}"`,
    )
  return content!
}

const { api, component } = createModal<RichShareModalData>(
  'RichShareModal',
  () => import('./RichShareModal.vue'),
  {
    resourceType: 'vizi',
    url: '',
    title: '',
    imageTitle: '',
    content: {},
    imageExtension: 'jpg',
    description: '',
    viewer: 'anonymous',
    modes: [],
    onSuccess: null,
  },
)

export const useRichShareModal = api
export default component
